import { Link } from "react-router-dom";
import { Shield, FileText, Users, Building, UserCheck, CreditCard, AlertTriangle, Lock, Scale, RefreshCw, Mail, ChevronRight } from "lucide-react";

const SECTIONS = [
  {
    icon: FileText,
    title: "Información General",
    content: (
      <>
        <p>
          Los presentes Términos y Condiciones regulan el uso del sitio web <strong className="text-white">plazafy.es</strong> (en adelante, &ldquo;la Plataforma&rdquo;), 
          operado por Plazafy (en adelante, &ldquo;nosotros&rdquo; o &ldquo;la Empresa&rdquo;).
        </p>
        <p>
          Al acceder y utilizar la Plataforma, el usuario acepta quedar vinculado por estos Términos y Condiciones, 
          así como por nuestra <Link to="/privacy" className="text-violet-400 hover:text-violet-300 underline decoration-violet-400/30 hover:decoration-violet-300 transition-colors">Política de Privacidad</Link>.
        </p>
      </>
    )
  },
  {
    icon: Shield,
    title: "Descripción del Servicio",
    content: (
      <>
        <p>Plazafy es una plataforma de intermediación que permite:</p>
        <ul className="list-none space-y-3 mt-3">
          <li className="flex items-start gap-3">
            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
            <span><strong className="text-white">A los propietarios:</strong> Publicar y gestionar el alquiler de plazas de garaje.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
            <span><strong className="text-white">A los usuarios:</strong> Buscar, reservar y alquilar plazas de garaje.</span>
          </li>
        </ul>
        <p className="mt-4">
          Plazafy actúa únicamente como intermediario entre propietarios e inquilinos, facilitando la conexión entre ambas partes. 
          <strong className="text-white"> No somos propietarios de las plazas de garaje ni parte en el contrato de alquiler</strong> que se establezca entre usuarios.
        </p>
      </>
    )
  },
  {
    icon: Users,
    title: "Registro de Usuarios",
    content: (
      <>
        <p>Para utilizar determinadas funcionalidades de la Plataforma, es necesario registrarse creando una cuenta de usuario.</p>
        <p className="mt-3">El usuario se compromete a:</p>
        <ul className="list-none space-y-2 mt-3">
          {["Proporcionar información veraz, actualizada y completa.", "Mantener la confidencialidad de sus credenciales de acceso.", "Notificar inmediatamente cualquier uso no autorizado de su cuenta.", "Ser mayor de 18 años o contar con autorización legal."].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    )
  },
  {
    icon: Building,
    title: "Obligaciones de los Propietarios",
    content: (
      <>
        <p>Los usuarios que publiquen plazas de garaje en la Plataforma declaran y garantizan que:</p>
        <ul className="list-none space-y-2 mt-3">
          {[
            "Son propietarios legítimos de la plaza o tienen autorización legal para alquilarla.",
            "La información publicada (ubicación, características, precios, fotografías) es veraz y exacta.",
            "La plaza cumple con todas las normativas aplicables (urbanísticas, de seguridad, etc.).",
            "Disponen de los seguros necesarios para cubrir posibles daños.",
            "Cumplirán con sus obligaciones fiscales derivadas del alquiler.",
            "Respetarán las reservas confirmadas y los términos acordados con los inquilinos."
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 p-4 bg-orange-500/5 border border-orange-500/20 rounded-xl">
          <p className="text-orange-400 text-sm">
            <strong>Importante:</strong> Plazafy no verifica la propiedad ni el estado de las plazas publicadas. 
            Los propietarios son los únicos responsables de la veracidad de la información publicada.
          </p>
        </div>
      </>
    )
  },
  {
    icon: UserCheck,
    title: "Obligaciones de los Inquilinos",
    content: (
      <>
        <p>Los usuarios que reserven plazas de garaje se comprometen a:</p>
        <ul className="list-none space-y-2 mt-3">
          {[
            "Utilizar la plaza únicamente para el estacionamiento de vehículos autorizados.",
            "Respetar las normas de la comunidad de propietarios y del edificio.",
            "No realizar actividades ilegales o molestas.",
            "Mantener la plaza en buen estado y comunicar cualquier incidencia.",
            "Respetar los horarios y condiciones acordados.",
            "Realizar los pagos en los plazos establecidos."
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    )
  },
  {
    icon: CreditCard,
    title: "Reservas y Pagos",
    content: (
      <>
        <p>Las reservas se consideran confirmadas una vez realizado el pago a través de la Plataforma.</p>
        
        <h4 className="text-white font-medium mt-5 mb-2">6.1 Métodos de pago</h4>
        <p>Aceptamos pagos mediante tarjeta de crédito/débito (procesados por Stripe) y PayPal.</p>
        
        <h4 className="text-white font-medium mt-5 mb-2">6.2 Política de cancelación</h4>
        <ul className="list-none space-y-2 mt-2">
          {[
            { bold: "Cancelación con más de 48 horas:", text: " Reembolso del 100%." },
            { bold: "Cancelación entre 24-48 horas:", text: " Reembolso del 50%." },
            { bold: "Cancelación con menos de 24 horas:", text: " Sin reembolso." }
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span><strong className="text-white">{item.bold}</strong>{item.text}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-sm">Los propietarios pueden establecer políticas de cancelación más flexibles.</p>

        <h4 className="text-white font-medium mt-5 mb-2">6.3 Comisiones</h4>
        <p>Plazafy cobra una comisión por cada transacción realizada a través de la Plataforma. 
           Esta comisión se detalla de forma transparente antes de confirmar cada reserva.</p>
      </>
    )
  },
  {
    icon: AlertTriangle,
    title: "Limitación de Responsabilidad",
    content: (
      <>
        <p>Plazafy actúa como mero intermediario y, por tanto:</p>
        <ul className="list-none space-y-2 mt-3">
          {[
            "No garantiza la disponibilidad, calidad o legalidad de las plazas publicadas.",
            "No es responsable de los daños o perjuicios derivados del uso de las plazas.",
            "No es responsable del incumplimiento de las obligaciones entre propietarios e inquilinos.",
            "No es responsable de robos, daños a vehículos u otros incidentes ocurridos en las plazas."
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm">
          Recomendamos a los usuarios contratar seguros adecuados para cubrir posibles contingencias.
        </p>
      </>
    )
  },
  {
    icon: Lock,
    title: "Propiedad Intelectual",
    content: (
      <>
        <p>
          Todos los contenidos de la Plataforma (textos, imágenes, logos, diseño, código fuente) 
          son propiedad de Plazafy o de sus licenciantes y están protegidos por las leyes de propiedad intelectual.
        </p>
        <p className="mt-3">
          Los usuarios que publiquen contenido en la Plataforma otorgan a Plazafy una licencia no exclusiva 
          para utilizar dicho contenido con fines de promoción y funcionamiento del servicio.
        </p>
      </>
    )
  },
  {
    icon: Shield,
    title: "Protección de Datos",
    content: (
      <>
        <p>
          El tratamiento de datos personales se realiza conforme al Reglamento General de Protección de Datos (RGPD) 
          y la Ley Orgánica de Protección de Datos (LOPD-GDD).
        </p>
        <p className="mt-3">
          Para más información, consulta nuestra <Link to="/privacy" className="text-violet-400 hover:text-violet-300 underline decoration-violet-400/30 hover:decoration-violet-300 transition-colors">Política de Privacidad</Link>.
        </p>
      </>
    )
  },
  {
    icon: Scale,
    title: "Resolución de Conflictos",
    content: (
      <>
        <p>
          En caso de conflicto entre usuarios, Plazafy podrá mediar de forma voluntaria, 
          pero no está obligada a resolver disputas entre propietarios e inquilinos.
        </p>
        <p className="mt-3">
          Para cualquier controversia derivada de estos Términos, las partes se someten a los 
          Juzgados y Tribunales de la ciudad de Madrid (España), con renuncia expresa a cualquier otro fuero.
        </p>
      </>
    )
  },
  {
    icon: RefreshCw,
    title: "Modificaciones",
    content: (
      <>
        <p>
          Plazafy se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. 
          Las modificaciones entrarán en vigor desde su publicación en la Plataforma.
        </p>
        <p className="mt-3">
          Se notificará a los usuarios registrados de cambios significativos a través del correo electrónico.
        </p>
      </>
    )
  },
  {
    icon: Mail,
    title: "Contacto",
    content: (
      <>
        <p>Para cualquier consulta relacionada con estos Términos y Condiciones, puede contactar con nosotros en:</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 p-4 bg-[#15121e] rounded-xl border border-white/5">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Email</p>
            <p className="text-white font-medium">hola@plazafy.es</p>
          </div>
          <div className="flex-1 p-4 bg-[#15121e] rounded-xl border border-white/5">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Web</p>
            <p className="text-white font-medium">www.plazafy.es</p>
          </div>
        </div>
      </>
    )
  }
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#1a1625] pt-24 pb-16" data-testid="terms-page">
      {/* Hero header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-500/5 to-transparent pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-violet-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white" data-testid="terms-title">
                Términos y Condiciones
              </h1>
              <p className="text-gray-500 text-sm mt-1">Última actualización: Diciembre 2025</p>
            </div>
          </div>
          <p className="text-gray-400 max-w-2xl">
            Estos términos regulan el uso de la plataforma Plazafy. Al usar nuestros servicios, aceptas estas condiciones.
          </p>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="space-y-4">
          {SECTIONS.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={section.title}
                className="group bg-[#231f30] rounded-2xl border border-white/5 hover:border-violet-500/20 transition-all duration-300 overflow-hidden"
                data-testid={`terms-section-${index + 1}`}
              >
                {/* Section header */}
                <div className="flex items-center gap-4 p-6 pb-0">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-violet-500/10 text-violet-400 font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-violet-400/60" />
                    <h2 className="text-lg font-semibold text-white">{section.title}</h2>
                  </div>
                </div>
                {/* Section content */}
                <div className="p-6 pt-4 text-gray-400 text-sm leading-relaxed space-y-2">
                  {section.content}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer navigation */}
        <div className="mt-12 p-6 bg-[#231f30] rounded-2xl border border-white/5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">Consulta también nuestra política de privacidad</p>
            <Link 
              to="/privacy" 
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-500/10 hover:bg-violet-500/20 text-violet-400 rounded-xl transition-colors text-sm font-medium"
              data-testid="terms-to-privacy-link"
            >
              Política de Privacidad
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
